'use client';

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useId } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button, type ButtonProps } from './button';
import type { ControlDensity, ControlSize } from './control-styles';

export interface DropdownMenuItem {
  label: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  disabledReason?: string;
  destructive?: boolean;
  separator?: boolean;
  checked?: boolean;
  radioGroup?: string;
  href?: string;
}

export interface DropdownMenuProps {
  trigger: React.ReactNode;
  items: DropdownMenuItem[];
  align?: 'left' | 'right';
  side?: 'top' | 'bottom';
  className?: string;
  navigate?: (href: string) => void;
  size?: ControlSize;
  density?: ControlDensity;
}

export function DropdownMenu({
  trigger,
  items,
  align = 'right',
  side = 'bottom',
  className,
  navigate,
  size,
  density,
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const keyboardHighlightRef = useRef(false);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const menu = menuRef.current;
    if (!menu) return;
    let offsetX = 0;
    let offsetY = 0;
    menu.style.transform = '';
    const keepInViewport = () => {
      // Recover the preferred alignment from the corrected bounds without
      // resetting styles on every scroll (including keyboard focus scrolling).
      const bounds = menu.getBoundingClientRect();
      const naturalLeft = bounds.left - offsetX;
      const naturalTop = bounds.top - offsetY;
      const gutter = 8;
      const left = Math.max(
        gutter,
        Math.min(naturalLeft, window.innerWidth - bounds.width - gutter),
      );
      const top = Math.max(
        gutter,
        Math.min(naturalTop, window.innerHeight - bounds.height - gutter),
      );
      offsetX = left - naturalLeft;
      offsetY = top - naturalTop;
      const transform = `translate(${offsetX}px, ${offsetY}px)`;
      if (menu.style.transform !== transform) menu.style.transform = transform;
    };
    keepInViewport();
    window.addEventListener('resize', keepInViewport);
    window.addEventListener('scroll', keepInViewport, true);
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(keepInViewport);
    observer?.observe(menu);
    return () => {
      window.removeEventListener('resize', keepInViewport);
      window.removeEventListener('scroll', keepInViewport, true);
      observer?.disconnect();
    };
  }, [isOpen, align, side]);

  useLayoutEffect(() => {
    if (!isOpen || highlightedIndex < 0) return;
    const menu = menuRef.current;
    const item = menu?.querySelectorAll<HTMLElement>('[role="menuitem"], [role="menuitemradio"]')[
      highlightedIndex
    ];
    if (!menu || !item) return;
    if (keyboardHighlightRef.current) item.focus({ preventScroll: true });
    const frame = menu.getBoundingClientRect();
    const row = item.getBoundingClientRect();
    if (row.top < frame.top) menu.scrollTop -= frame.top - row.top;
    else if (row.bottom > frame.bottom) menu.scrollTop += row.bottom - frame.bottom;
  }, [isOpen, highlightedIndex]);

  const close = useCallback(() => {
    if (menuRef.current?.contains(document.activeElement))
      containerRef.current?.querySelector<HTMLElement>('[aria-haspopup="menu"]')?.focus();
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((open) => !open);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  const actionableItems = items.filter((item) => !item.separator);

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    keyboardHighlightRef.current = true;
    const menuItems = [
      ...(menuRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"], [role="menuitemradio"]',
      ) ?? []),
    ];
    const focusedIndex = menuItems.indexOf(document.activeElement as HTMLElement);
    const firstEligible = actionableItems.findIndex(
      (item) => !item.disabled || item.disabledReason,
    );
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0) {
          menuItems[focusedIndex]?.click();
        } else if (!isOpen) {
          setHighlightedIndex(firstEligible);
          setIsOpen(true);
        } else if (highlightedIndex >= 0) {
          const item = actionableItems[highlightedIndex];
          if (item && !item.disabled) {
            if (item.href) {
              if (navigate) {
                navigate(item.href);
              } else {
                window.location.assign(item.href);
              }
            } else {
              item.onClick?.();
            }
            close();
          }
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setHighlightedIndex(firstEligible);
          setIsOpen(true);
        } else {
          {
            let next = (focusedIndex >= 0 ? focusedIndex : highlightedIndex) + 1;
            while (
              next < actionableItems.length &&
              actionableItems[next]?.disabled &&
              !actionableItems[next]?.disabledReason
            )
              next++;
            if (next < actionableItems.length) {
              setHighlightedIndex(next);
              menuItems[next]?.focus({ preventScroll: true });
            }
          }
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          {
            let next = (focusedIndex >= 0 ? focusedIndex : highlightedIndex) - 1;
            while (
              next >= 0 &&
              actionableItems[next]?.disabled &&
              !actionableItems[next]?.disabledReason
            )
              next--;
            if (next >= 0) {
              setHighlightedIndex(next);
              menuItems[next]?.focus({ preventScroll: true });
            }
          }
        }
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          close();
          containerRef.current?.querySelector<HTMLElement>('[aria-haspopup="menu"]')?.focus();
        }
        break;
      default:
        break;
    }
  };

  const triggerId =
    (React.isValidElement<{ id?: string }>(trigger) && trigger.props.id) || `${menuId}-trigger`;
  const triggerAriaProps = {
    id: triggerId,
    'aria-haspopup': 'menu' as const,
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? menuId : undefined,
  };

  const styledTrigger =
    React.isValidElement<ButtonProps>(trigger) && trigger.type === 'button' ? (
      <Button {...trigger.props} variant="outline" size={size ?? 'toolbar'} density={density} />
    ) : (
      trigger
    );

  const renderedTrigger = React.isValidElement(styledTrigger) ? (
    React.cloneElement(styledTrigger, {
      ...triggerAriaProps,
      ...(styledTrigger.type === Button
        ? { ...(size ? { size } : {}), ...(density ? { density } : {}) }
        : {}),
      onClick: (e: React.MouseEvent) => {
        toggle();
        (styledTrigger.props as { onClick?: (e: React.MouseEvent) => void }).onClick?.(e);
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        handleTriggerKeyDown(e);
        (styledTrigger.props as { onKeyDown?: (e: React.KeyboardEvent) => void }).onKeyDown?.(e);
      },
    } as Record<string, unknown>)
  ) : (
    <Button
      type="button"
      variant="outline"
      size={size ?? 'toolbar'}
      density={density}
      {...triggerAriaProps}
      onClick={toggle}
      onKeyDown={handleTriggerKeyDown}
    >
      {trigger}
    </Button>
  );

  let actionableIndex = -1;

  const renderMenuItem = (item: DropdownMenuItem, idx: number) => {
    if (item.separator) {
      return <div key={`sep-${idx}`} className="my-1 h-px bg-border" role="separator" />;
    }

    actionableIndex++;
    const currentActionIndex = actionableIndex;
    const role = typeof item.checked === 'boolean' ? 'menuitemradio' : 'menuitem';
    const itemClassName = cn(
      'flex min-h-9 w-full justify-start min-w-0 items-center gap-2 px-3 py-2 text-left font-body text-body-sm transition-interactive duration-200',
      currentActionIndex === highlightedIndex && 'bg-surface-soft',
      item.destructive
        ? 'border-t border-border text-status-danger-fg hover:bg-status-danger-bg'
        : 'text-text-heading',
      item.disabled && 'cursor-not-allowed opacity-50',
    );
    const itemContent = (
      <>
        {item.icon && <span className="shrink-0 [&_svg]:h-4 [&_svg]:w-4">{item.icon}</span>}
        <span className="min-w-0 break-words">{item.label}</span>
        {typeof item.checked === 'boolean' && (
          <Check
            aria-hidden="true"
            className={cn('ml-auto h-4 w-4 shrink-0', !item.checked && 'opacity-0')}
          />
        )}
      </>
    );

    if (item.href && !item.disabled) {
      return (
        <a
          key={`item-${idx}`}
          href={item.href}
          role={role}
          aria-checked={typeof item.checked === 'boolean' ? item.checked : undefined}
          onClick={(event) => {
            if (
              navigate &&
              event.button === 0 &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.shiftKey &&
              !event.altKey
            ) {
              event.preventDefault();
              navigate(item.href as string);
            }
            close();
          }}
          onMouseEnter={() => {
            keyboardHighlightRef.current = false;
            setHighlightedIndex(currentActionIndex);
          }}
          className={itemClassName}
        >
          {itemContent}
        </a>
      );
    }

    return (
      <Button
        variant="ghost"
        key={`item-${idx}`}
        type="button"
        role={role}
        aria-checked={typeof item.checked === 'boolean' ? item.checked : undefined}
        disabled={item.disabled}
        disabledReason={item.disabled ? item.disabledReason : undefined}
        onClick={() => {
          if (!item.disabled) {
            item.onClick?.();
            close();
          }
        }}
        onMouseEnter={() => {
          keyboardHighlightRef.current = false;
          setHighlightedIndex(currentActionIndex);
        }}
        className={itemClassName}
      >
        {itemContent}
      </Button>
    );
  };

  return (
    <div ref={containerRef} className={cn('relative inline-block min-w-0', className)}>
      {renderedTrigger}

      {isOpen && (
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          aria-labelledby={triggerId}
          onKeyDown={handleTriggerKeyDown}
          className={cn(
            'absolute z-50 min-w-[min(180px,calc(100vw-1rem))] overflow-auto rounded-overlay border border-border bg-surface-sheet py-1 shadow-popover',
            'max-w-[calc(100vw-1rem)] max-h-[calc(100dvh-1rem)]',
            align === 'right' ? 'right-0' : 'left-0',
            side === 'top' ? 'bottom-full mb-1' : 'mt-1',
          )}
        >
          {items.map((item, idx) => {
            if (!item.radioGroup) return renderMenuItem(item, idx);
            if (idx > 0 && items[idx - 1]?.radioGroup === item.radioGroup) return null;

            const groupedItems: Array<{ item: DropdownMenuItem; idx: number }> = [];
            for (let groupIndex = idx; groupIndex < items.length; groupIndex++) {
              const groupedItem = items[groupIndex];
              if (!groupedItem || groupedItem.radioGroup !== item.radioGroup) break;
              groupedItems.push({ item: groupedItem, idx: groupIndex });
            }

            return (
              <div
                key={`group-${item.radioGroup}-${idx}`}
                role="group"
                aria-label={item.radioGroup}
              >
                {groupedItems.map(({ item: groupedItem, idx: groupedIndex }) =>
                  renderMenuItem(groupedItem, groupedIndex),
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
